import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  IconButton,
  FormControlLabel,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SaveIcon from '@mui/icons-material/Save';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getGeneralConfig,
  getRestaurantSettings,
  saveRestaurantSettings,
  updateRestaurantBranch,
} from '../../../services/ecommerceApi';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';
import BranchTemplatePicker, {
  normalizeBranchPageTemplate,
  type BranchPageTemplate,
} from './BranchTemplatePicker';
import BranchColorPicker from './BranchColorPicker';
import { geocodeBranchMapUrl, geocodeSearch } from '../../../utils/geocodeBranchAddress';
import { deriveSitePalette } from '../utils/logoColor';
import GuideButton from '../../common/guide/GuideButton';

interface BranchSettingsComponentProps {
  companyId: string | null;
  configId: string;
  onSettingsChange?: (settings: any) => void;
}

/**
 * Şube ayarları paneli (Branches e-ticaret görünüm yönetimi).
 *
 * Şube modülü tamamen `Branches` tablosundan çalışır.
 * Bu component şubelerin e-ticaret görünüm alanlarını
 * (görsel, harita, sıra, çalışma saatleri, e-ticarette göster) düzenler.
 * Şube ekleme/silme `Branches` yönetim sayfasından yapılır.
 * Şubeler sayfasında girilen adres, boşsa harita konumuna otomatik yazılır.
 */
interface BranchRow {
  id: string;
  branchId: number;
  city: string;
  district?: string;
  branchName: string;
  address?: string;
  phone?: string;
  workingHours?: string;
  isOpen24Hours?: boolean;
  imageUrl?: string;
  mapUrl?: string;
  displayOrder: number;
  showOnEcommerce: boolean;
}

interface BranchCardLogo {
  url: string;
  alt?: string;
}

interface BranchPageSettings {
  pageTemplate: BranchPageTemplate;
  heroEnabled: boolean;
  heroTitle: string;
  heroDescription: string;
  heroBackgroundColor: string;
  mapAccentColor: string;
  brandLogos: BranchCardLogo[];
  paymentIcons: BranchCardLogo[];
}

interface MapSearchResultItem {
  lat: number;
  lng: number;
  label: string;
}

interface MapPointItem {
  id: string;
  name: string;
  city: string;
  lat: number;
  lng: number;
}

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const parseCoordsFromGoogleMapsUrl = (raw?: string): { lat: number; lng: number } | null => {
  const value = String(raw || '').trim();
  if (!value) return null;

  const decoded = (() => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  })();

  const atMatch = decoded.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i);
  if (atMatch) {
    const lat = Number(atMatch[1]);
    const lng = Number(atMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const qMatch = decoded.match(/[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i);
  if (qMatch) {
    const lat = Number(qMatch[1]);
    const lng = Number(qMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const dMatch = decoded.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/i);
  if (dMatch) {
    const lat = Number(dMatch[1]);
    const lng = Number(dMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  return null;
};

const toGoogleMapsPointUrl = (lat: number, lng: number): string =>
  `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`;

const normalizeMapUrlInput = (value?: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^www\./i.test(raw)) return `https://${raw}`;
  if (/^maps\.app\.goo\.gl\//i.test(raw)) return `https://${raw}`;
  if (/^[A-Za-z0-9_-]{10,}$/.test(raw)) return `https://maps.app.goo.gl/${raw}`;

  return raw;
};

const normalizeColorHex = (value?: string): string => {
  const raw = String(value || '').trim();
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(raw) ? raw : '#f8fafc';
};

const normalizeOptionalColorHex = (value?: string): string => {
  const raw = String(value || '').trim();
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(raw) ? raw : '';
};

const MAP_COLOR_MARKER = /<!--gts-map:(#[0-9A-Fa-f]{3}|#[0-9A-Fa-f]{6})-->/g;

const stripMapColorMarker = (value?: string): string =>
  String(value || '').replace(MAP_COLOR_MARKER, '').trim();

const readMapColorMarker = (value?: string): string =>
  String(value || '').match(/<!--gts-map:(#[0-9A-Fa-f]{3}|#[0-9A-Fa-f]{6})-->/)?.[1] || '';

const withMapColorMarker = (description: string, color?: string): string => {
  const clean = stripMapColorMarker(description) || 'Şehirlere göre şubelerimizi keşfedin.';
  const hex = normalizeOptionalColorHex(color);
  return hex ? `${clean}<!--gts-map:${hex}-->` : clean;
};

const readApiText = (data: Record<string, any>, camel: string, pascal: string, fallback = ''): string =>
  String(data?.[camel] ?? data?.[pascal] ?? fallback);

const SIMPLE_MAP_COLORS = [
  { key: 'orange', label: 'Turuncu', value: '#f27a1a' },
  { key: 'navy', label: 'Lacivert', value: '#1e3a5f' },
  { key: 'blue', label: 'Mavi', value: '#2563eb' },
  { key: 'red', label: 'Kırmızı', value: '#dc2626' },
  { key: 'green', label: 'Yeşil', value: '#15803d' },
  { key: 'teal', label: 'Turkuaz', value: '#0d9488' },
  { key: 'purple', label: 'Mor', value: '#7c3aed' },
  { key: 'slate', label: 'Antrasit', value: '#475569' },
];

const SIMPLE_BG_COLORS = [
  { key: 'white', label: 'Beyaz', value: '#ffffff' },
  { key: 'light', label: 'Açık gri', value: '#f8fafc' },
  { key: 'gray', label: 'Gri', value: '#ebebeb' },
  { key: 'cream', label: 'Krem', value: '#fff7ed' },
  { key: 'softBlue', label: 'Açık mavi', value: '#eff6ff' },
  { key: 'softGreen', label: 'Açık yeşil', value: '#f0fdf4' },
  { key: 'softPink', label: 'Açık pembe', value: '#fff1f2' },
];

const DEFAULT_SITE_COLOR = '#2563eb';

const normalizeLogoList = (value: unknown): BranchCardLogo[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') {
        const url = item.trim();
        return url ? { url } : null;
      }
      const url = String((item as any)?.url || (item as any)?.Url || '').trim();
      if (!url) return null;
      const alt = String((item as any)?.alt || (item as any)?.Alt || '').trim();
      return alt ? { url, alt } : { url };
    })
    .filter((x): x is BranchCardLogo => Boolean(x));
};

const normalizeTr = (value?: string): string =>
  String(value || '')
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const DISTRICT_OR_CITY_CENTER: Record<string, { lat: number; lng: number }> = {
  'istanbul': { lat: 41.0082, lng: 28.9784 },
  'ankara': { lat: 39.9334, lng: 32.8597 },
  'bursa': { lat: 40.195, lng: 29.06 },
  'duzce': { lat: 40.8438, lng: 31.1565 },
  'düzce': { lat: 40.8438, lng: 31.1565 },
  'bolu': { lat: 40.7395, lng: 31.6116 },
  'kocaeli': { lat: 40.7667, lng: 29.9167 },
  'mugla': { lat: 37.2153, lng: 28.3636 },
  'muğla': { lat: 37.2153, lng: 28.3636 },
  'sivas': { lat: 39.7477, lng: 37.0179 },
  'yalova': { lat: 40.655, lng: 29.2769 },
  'sakarya': { lat: 40.7569, lng: 30.3781 },
  'adapazari': { lat: 40.7569, lng: 30.3781 },
  'adapazarı': { lat: 40.7569, lng: 30.3781 },
  'maltepe': { lat: 40.9351, lng: 29.1557 },
  'basaksehir': { lat: 41.0931, lng: 28.8020 },
  'başakşehir': { lat: 41.0931, lng: 28.8020 },
  'merkez': { lat: 39.0, lng: 35.0 },
};

const resolveDistrictCityCenter = (district?: string, city?: string): { lat: number; lng: number } => {
  const districtKey = normalizeTr(district);
  const cityKey = normalizeTr(city);
  const isGenericDistrict = districtKey === 'merkez' || districtKey === 'center';
  if (!isGenericDistrict && DISTRICT_OR_CITY_CENTER[districtKey]) {
    return DISTRICT_OR_CITY_CENTER[districtKey];
  }
  return DISTRICT_OR_CITY_CENTER[cityKey] || DISTRICT_OR_CITY_CENTER[districtKey] || { lat: 39.0, lng: 35.0 };
};

const MapClickHandler: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  const map = useMap();

  useEffect(() => {
    const handleClick = (e: any) => {
      onPick(e.latlng.lat, e.latlng.lng);
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onPick]);

  return null;
};

const FitBounds: React.FC<{ points: Array<{ lat: number; lng: number }> }> = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    const canMoveMap = Boolean((map as any)?._loaded) && Boolean((map as any)?._mapPane);
    if (!canMoveMap) {
      return;
    }

    if (!points.length) {
      map.setView([39.0, 35.0], 6, { animate: false });
      return;
    }

    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 15, { animate: false });
      return;
    }

    const bounds = (L as any).latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds.pad(0.2), { maxZoom: 14, animate: false });
  }, [map, points]);

  return null;
};

const RecenterMap: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();

  useEffect(() => {
    const canMoveMap = Boolean((map as any)?._loaded) && Boolean((map as any)?._mapPane);
    if (!canMoveMap) {
      return;
    }
    map.setView([lat, lng], Math.max(map.getZoom(), 13), { animate: false });
  }, [map, lat, lng]);

  return null;
};

const BranchSettingsComponent: React.FC<BranchSettingsComponentProps> = ({
  companyId,
  configId,
  onSettingsChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [confirmPassiveOpen, setConfirmPassiveOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalMode, setImageModalMode] = useState<'branch' | 'brands' | 'payments'>('branch');
  const [imageTargetRowId, setImageTargetRowId] = useState<number | null>(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [mapTargetRowId, setMapTargetRowId] = useState<number | null>(null);
  const [mapDraftCoords, setMapDraftCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [mapSearchError, setMapSearchError] = useState<string | null>(null);
  const [mapSearchResults, setMapSearchResults] = useState<MapSearchResultItem[]>([]);
  const [branchPageSettings, setBranchPageSettings] = useState<BranchPageSettings>({
    pageTemplate: 'classic',
    heroEnabled: true,
    heroTitle: 'Şubeler',
    heroDescription: 'Şehirlere göre şubelerimizi keşfedin.',
    heroBackgroundColor: '#f8fafc',
    mapAccentColor: '',
    brandLogos: [],
    paymentIcons: [],
  });
  const [savingPageSettings, setSavingPageSettings] = useState(false);
  const [fillingMaps, setFillingMaps] = useState(false);
  const [fillingMapsProgress, setFillingMapsProgress] = useState('');
  const [sitePrimaryColor, setSitePrimaryColor] = useState(DEFAULT_SITE_COLOR);

  const savedCities = useMemo(() => {
    const set = new Set<string>();
    branches.forEach((x) => {
      if (x.city?.trim()) set.add(x.city.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [branches]);

  const statusCounts = useMemo(() => ({
    all: branches.length,
    active: branches.filter((b) => b.showOnEcommerce).length,
    inactive: branches.filter((b) => !b.showOnEcommerce).length,
  }), [branches]);

  const filteredBranches = useMemo(() => {
    const q = searchTerm.trim().toLocaleLowerCase('tr');
    return branches.filter((b) => {
      if (statusFilter === 'active' && !b.showOnEcommerce) return false;
      if (statusFilter === 'inactive' && b.showOnEcommerce) return false;
      if (cityFilter !== 'all' && b.city.trim() !== cityFilter) return false;
      if (
        q &&
        ![b.branchName, b.city, b.district, b.address, b.phone].some((field) =>
          String(field || '').toLocaleLowerCase('tr').includes(q)
        )
      ) {
        return false;
      }
      return true;
    });
  }, [branches, searchTerm, statusFilter, cityFilter]);

  const bulkTargets = useMemo(
    () =>
      selectedIds.size > 0
        ? filteredBranches.filter((b) => selectedIds.has(b.branchId))
        : filteredBranches,
    [filteredBranches, selectedIds]
  );
  const inactiveTargets = useMemo(
    () => bulkTargets.filter((b) => !b.showOnEcommerce),
    [bulkTargets]
  );
  const activeTargets = useMemo(
    () => bulkTargets.filter((b) => b.showOnEcommerce),
    [bulkTargets]
  );
  const allFilteredSelected =
    filteredBranches.length > 0 && filteredBranches.every((b) => selectedIds.has(b.branchId));
  const someFilteredSelected = filteredBranches.some((b) => selectedIds.has(b.branchId));

  const totalPages = Math.max(1, Math.ceil(filteredBranches.length / rowsPerPage));
  const imageTargetRow = useMemo(
    () => branches.find((x) => x.branchId === imageTargetRowId) || null,
    [branches, imageTargetRowId]
  );
  const mapTargetRow = useMemo(
    () => branches.find((x) => x.branchId === mapTargetRowId) || null,
    [branches, mapTargetRowId]
  );
  const previewMapPoints = useMemo<MapPointItem[]>(
    () =>
      filteredBranches
        .filter((row) => row.showOnEcommerce)
        .map((row) => {
          const coords = parseCoordsFromGoogleMapsUrl(normalizeMapUrlInput(row.mapUrl));
          return {
            id: row.id,
            name: row.branchName,
            city: row.city,
            lat: (coords || resolveDistrictCityCenter(row.district, row.city)).lat,
            lng: (coords || resolveDistrictCityCenter(row.district, row.city)).lng,
          };
        })
        .filter((x): x is MapPointItem => x !== null),
    [filteredBranches]
  );
  const pagedBranches = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredBranches.slice(start, start + rowsPerPage);
  }, [filteredBranches, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [searchTerm, statusFilter, cityFilter]);

  const loadData = async () => {
    if (!companyId || !configId) return;
    try {
      setLoading(true);
      const response = await getRestaurantSettings(companyId, configId);
      if (response?.success && response?.data) {
        const data = response.data;
        const rawDescription = readApiText(data, 'heroDescription', 'HeroDescription');
        setBranchPageSettings({
          pageTemplate: normalizeBranchPageTemplate(data.pageTemplate || data.templateType),
          heroEnabled: data.heroEnabled !== false && data.HeroEnabled !== false,
          heroTitle: readApiText(data, 'heroTitle', 'HeroTitle', 'Şubeler'),
          heroDescription: stripMapColorMarker(rawDescription) || 'Şehirlere göre şubelerimizi keşfedin.',
          heroBackgroundColor: normalizeColorHex(data.heroBackgroundColor || data.HeroBackgroundColor),
          mapAccentColor:
            normalizeOptionalColorHex(data.mapAccentColor || data.MapAccentColor) ||
            readMapColorMarker(rawDescription),
          brandLogos: normalizeLogoList(data.brandLogos || data.BrandLogos),
          paymentIcons: normalizeLogoList(data.paymentIcons || data.PaymentIcons),
        });
        const rawLocations: any[] = Array.isArray(data.locations) ? data.locations : [];
        const mapped: BranchRow[] = rawLocations.map((b) => ({
          id: String(b.id ?? ''),
          branchId: Number(b.branchId ?? 0),
          city: b.city || '',
          district: b.district || '',
          branchName: b.branchName || '',
          address: b.address || '',
          phone: b.phone || '',
          workingHours: b.workingHours || b.WorkingHours || '',
          isOpen24Hours: b.isOpen24Hours === true || b.IsOpen24Hours === true,
          imageUrl: b.imageUrl || '',
          mapUrl: b.mapUrl || '',
          displayOrder: Number(b.displayOrder ?? 0),
          showOnEcommerce: b.showOnEcommerce === true,
        }));
        setBranches(mapped);
        setError(null);
        if (onSettingsChange) onSettingsChange(data);
      }
    } catch (e) {
      console.error(e);
      setError('Şube ayarları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [companyId, configId]);

  useEffect(() => {
    if (!companyId) return;
    void getGeneralConfig(companyId)
      .then((response) => {
        const color = normalizeOptionalColorHex(response?.data?.primaryColor);
        if (color) setSitePrimaryColor(color);
      })
      .catch(() => undefined);
  }, [companyId]);

  const updateBranchField = (branchId: number, patch: Partial<BranchRow>) => {
    setBranches((prev) => prev.map((x) => (x.branchId === branchId ? { ...x, ...patch } : x)));
  };

  const savePageSettings = async (override?: Partial<BranchPageSettings>) => {
    if (!configId) return;
    const next = { ...branchPageSettings, ...override };
    try {
      setSavingPageSettings(true);
      setError(null);
      await saveRestaurantSettings({
        configId,
        pageTemplate: next.pageTemplate,
        heroEnabled: next.heroEnabled,
        heroTitle: next.heroTitle,
        heroDescription: withMapColorMarker(next.heroDescription, next.mapAccentColor),
        heroBackgroundColor: normalizeColorHex(next.heroBackgroundColor),
        mapAccentColor: normalizeOptionalColorHex(next.mapAccentColor),
        brandLogos: next.brandLogos,
        paymentIcons: next.paymentIcons,
      });
      setBranchPageSettings(next);
      setSuccess('Şube sayfası görünüm ayarları kaydedildi.');
      setTimeout(() => setSuccess(null), 2000);
      if (onSettingsChange) {
        onSettingsChange({
          pageTemplate: next.pageTemplate,
          heroEnabled: next.heroEnabled,
          heroTitle: next.heroTitle,
          heroDescription: next.heroDescription,
          heroBackgroundColor: next.heroBackgroundColor,
          mapAccentColor: next.mapAccentColor,
          brandLogos: next.brandLogos,
          paymentIcons: next.paymentIcons,
        });
      }
    } catch (e) {
      console.error(e);
      const apiMessage =
        (e as any)?.response?.data?.message ||
        (e as any)?.response?.data?.Message ||
        (e as any)?.message;
      setError(apiMessage ? `Şube sayfası ayarları kaydedilemedi: ${apiMessage}` : 'Şube sayfası ayarları kaydedilemedi.');
    } finally {
      setSavingPageSettings(false);
    }
  };

  const buildBranchUpdatePayload = (row: BranchRow, override?: { showOnEcommerce?: boolean; mapUrl?: string | null }) => ({
    imageUrl: row.imageUrl?.trim() || null,
    mapUrl: override && 'mapUrl' in override ? override.mapUrl ?? null : (normalizeMapUrlInput(row.mapUrl) || null),
    displayOrder: Number(row.displayOrder || 0),
    showOnEcommerce: override?.showOnEcommerce ?? row.showOnEcommerce,
    workingHours: row.isOpen24Hours ? '' : (row.workingHours || ''),
    isOpen24Hours: Boolean(row.isOpen24Hours),
  });

  const saveBranchRow = async (row: BranchRow) => {
    if (!row.branchId) return;
    try {
      setSavingRow(row.branchId);
      setError(null);
      let normalizedMapUrl = normalizeMapUrlInput(row.mapUrl);
      if (!normalizedMapUrl && row.address) {
        const geocoded = await geocodeBranchMapUrl({
          address: row.address,
          district: row.district,
          city: row.city,
        });
        if (geocoded) {
          normalizedMapUrl = geocoded;
          updateBranchField(row.branchId, { mapUrl: geocoded });
        }
      }
      const mapUrlToSave: string | null = normalizedMapUrl || null;

      await updateRestaurantBranch(String(row.branchId), buildBranchUpdatePayload(row, { mapUrl: mapUrlToSave }));
      setSuccess(`"${row.branchName}" şubesi güncellendi.`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (e) {
      console.error(e);
      setError('Şube güncellenemedi.');
    } finally {
      setSavingRow(null);
    }
  };

  const toggleRowSelected = (branchId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(branchId);
      else next.delete(branchId);
      return next;
    });
  };

  const toggleSelectAllFiltered = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredBranches.map((b) => b.branchId)));
  };

  const applyBulkVisibility = async (show: boolean) => {
    const rows = show ? inactiveTargets : activeTargets;
    if (rows.length === 0) return;

    try {
      setBulkSaving(true);
      setError(null);
      for (const row of rows) {
        updateBranchField(row.branchId, { showOnEcommerce: show });
        await updateRestaurantBranch(String(row.branchId), buildBranchUpdatePayload(row, { showOnEcommerce: show }));
      }
      setSuccess(
        show
          ? `${rows.length} pasif şube e-ticarette gösterilecek şekilde açıldı.`
          : `${rows.length} şube e-ticarette gizlendi.`
      );
      setTimeout(() => setSuccess(null), 3000);
      setSelectedIds(new Set());
      setConfirmPassiveOpen(false);
    } catch (e) {
      console.error(e);
      setError('Toplu işlem tamamlanamadı. Liste yenileniyor.');
      await loadData();
    } finally {
      setBulkSaving(false);
    }
  };

  const fillMissingMapsFromAddresses = async () => {
    const missing = branches.filter((row) => !normalizeMapUrlInput(row.mapUrl) && row.address?.trim());
    if (missing.length === 0) {
      setSuccess('Adresi olup konumu boş olan şube yok.');
      setTimeout(() => setSuccess(null), 2000);
      return;
    }

    try {
      setFillingMaps(true);
      setError(null);
      let filled = 0;
      for (let i = 0; i < missing.length; i += 1) {
        const row = missing[i];
        setFillingMapsProgress(`${i + 1}/${missing.length}`);
        const geocoded = await geocodeBranchMapUrl({
          address: row.address,
          district: row.district,
          city: row.city,
        });
        if (geocoded) {
          updateBranchField(row.branchId, { mapUrl: geocoded });
          await updateRestaurantBranch(String(row.branchId), {
            imageUrl: row.imageUrl?.trim() || null,
            mapUrl: geocoded,
            displayOrder: Number(row.displayOrder || 0),
            showOnEcommerce: row.showOnEcommerce,
            workingHours: row.isOpen24Hours ? '' : (row.workingHours || ''),
            isOpen24Hours: Boolean(row.isOpen24Hours),
          });
          filled += 1;
        }
      }
      const failed = missing.length - filled;
      if (filled === 0) {
        setError('Adreslerden konum bulunamadı. Haritadan Seç ile işaretleyebilirsiniz.');
      } else {
        setSuccess(
          failed > 0
            ? `${filled} şubenin konumu dolduruldu, ${failed} adreste konum bulunamadı.`
            : `${filled} şubenin harita konumu adresten dolduruldu.`
        );
        setTimeout(() => setSuccess(null), 4000);
      }
    } catch (e) {
      console.error(e);
      setError('Eksik konumlar doldurulamadı.');
    } finally {
      setFillingMaps(false);
      setFillingMapsProgress('');
    }
  };

  const openImageLibrary = (branchId: number) => {
    setImageModalMode('branch');
    setImageTargetRowId(branchId);
    setImageModalOpen(true);
  };

  const openLogoLibrary = (mode: 'brands' | 'payments') => {
    setImageModalMode(mode);
    setImageTargetRowId(null);
    setImageModalOpen(true);
  };

  const closeImageLibrary = () => {
    setImageModalOpen(false);
    setImageTargetRowId(null);
    setImageModalMode('branch');
  };

  const handleImageLibrarySave = (selectedUrls: string[]) => {
    if (imageModalMode === 'brands' || imageModalMode === 'payments') {
      const key = imageModalMode === 'brands' ? 'brandLogos' : 'paymentIcons';
      setBranchPageSettings((prev) => ({
        ...prev,
        [key]: selectedUrls
          .map((url) => url.trim())
          .filter(Boolean)
          .map((url) => prev[key].find((x) => x.url === url) || { url }),
      }));
      closeImageLibrary();
      return;
    }
    if (!imageTargetRowId) return;
    updateBranchField(imageTargetRowId, { imageUrl: selectedUrls[0] || '' });
    closeImageLibrary();
  };

  const moveLogo = (key: 'brandLogos' | 'paymentIcons', index: number, direction: -1 | 1) => {
    setBranchPageSettings((prev) => {
      const next = [...prev[key]];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return { ...prev, [key]: next };
    });
  };

  const removeLogo = (key: 'brandLogos' | 'paymentIcons', index: number) => {
    setBranchPageSettings((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  const openMapPicker = (row: BranchRow) => {
    const parsed = parseCoordsFromGoogleMapsUrl(row.mapUrl);
    setMapTargetRowId(row.branchId);
    setMapDraftCoords(parsed || { lat: 39.0, lng: 35.0 });
    setMapSearchQuery(
      [row.address, row.district, row.city, 'Türkiye'].filter(Boolean).join(', ')
      || [row.branchName, row.district, row.city, 'Türkiye'].filter(Boolean).join(', ')
    );
    setMapSearchError(null);
    setMapSearchResults([]);
    setMapPickerOpen(true);
  };

  const closeMapPicker = () => {
    setMapPickerOpen(false);
    setMapTargetRowId(null);
    setMapDraftCoords(null);
    setMapSearchQuery('');
    setMapSearchError(null);
    setMapSearchLoading(false);
    setMapSearchResults([]);
  };

  const saveMapLocation = () => {
    if (!mapTargetRowId || !mapDraftCoords) return;
    updateBranchField(mapTargetRowId, {
      mapUrl: toGoogleMapsPointUrl(mapDraftCoords.lat, mapDraftCoords.lng),
    });
    closeMapPicker();
  };

  const searchLocationOnMap = async (overrideQuery?: string) => {
    const query = (overrideQuery ?? mapSearchQuery).trim();
    if (!query) {
      setMapSearchError('Lütfen aranacak bir konum yazın.');
      return;
    }

    try {
      setMapSearchLoading(true);
      setMapSearchError(null);
      setMapSearchResults([]);

      const results = await geocodeSearch(
        {
          address: mapTargetRow?.address,
          district: mapTargetRow?.district,
          city: mapTargetRow?.city,
        },
        query,
        8
      );

      if (results.length === 0) {
        setMapSearchError('Konum bulunamadı. Daha açık bir adres yazın veya haritaya tıklayın.');
        return;
      }

      setMapSearchResults(results);
      setMapDraftCoords({ lat: results[0].lat, lng: results[0].lng });
    } catch (error) {
      console.error('Map search failed:', error);
      setMapSearchError('Arama sırasında hata oluştu. Tekrar deneyin.');
    } finally {
      setMapSearchLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 4 }}><Typography>Yükleniyor...</Typography></Box>;
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
          <StorefrontIcon sx={{ mr: 1 }} />
          Şube Görünümü
          <GuideButton guideKey="ecommerce.branches" size="small" sx={{ ml: 0.5 }} />
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Şube Sayfası Şablonu
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Şablona tıklayınca kaydedilir. Vitrinde görmek için şubeler sayfasını yenileyin.
        </Typography>
        <BranchTemplatePicker
          value={branchPageSettings.pageTemplate}
          mapAccentColor={normalizeOptionalColorHex(branchPageSettings.mapAccentColor) || sitePrimaryColor}
          onChange={(pageTemplate) => {
            setBranchPageSettings((prev) => ({ ...prev, pageTemplate }));
            void savePageSettings({ pageTemplate });
          }}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Şube Sayfası Bölüm Ayarları
        </Typography>
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={branchPageSettings.heroEnabled}
                onChange={(e) => setBranchPageSettings((prev) => ({ ...prev, heroEnabled: e.target.checked }))}
              />
            }
            label="Üst Başlık Alanını Göster (Şubeler / açıklama)"
            sx={{ m: 0 }}
          />
        </Box>

        {branchPageSettings.heroEnabled && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                value={branchPageSettings.heroTitle}
                onChange={(e) =>
                  setBranchPageSettings((prev) => ({ ...prev, heroTitle: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Açıklama"
                value={branchPageSettings.heroDescription}
                onChange={(e) =>
                  setBranchPageSettings((prev) => ({ ...prev, heroDescription: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <BranchColorPicker
                label="Üst alan arka planı"
                helperText="Başlık bölümünün arka planı. İki şablonda da aynı ayar kullanılır."
                value={normalizeColorHex(branchPageSettings.heroBackgroundColor)}
                onChange={(heroBackgroundColor) => {
                  setBranchPageSettings((prev) => ({ ...prev, heroBackgroundColor }));
                  void savePageSettings({ heroBackgroundColor });
                }}
                recommended={{
                  label: 'Önerilen site rengi',
                  value: deriveSitePalette(sitePrimaryColor).heroSolid,
                  hint: 'Sitenizin ana renginden üretilen açık arka plan',
                }}
                options={SIMPLE_BG_COLORS}
              />
            </Grid>
          </Grid>
        )}

        <Box sx={{ mb: 1, mt: branchPageSettings.heroEnabled ? 0.5 : 1 }}>
          <BranchColorPicker
            label={
              branchPageSettings.pageTemplate === 'province-map'
                ? 'Harita rengi'
                : 'Harita ve vurgu rengi'
            }
            helperText={
              branchPageSettings.pageTemplate === 'province-map'
                ? 'İl haritasında boyalı illerin rengi. Klasik şablonda pin ve filtre rengi olarak da kullanılır.'
                : 'Klasik şablonda pin, filtre ve kart vurgu rengi. İl haritası şablonunda boyalı iller için de kullanılır.'
            }
            value={normalizeOptionalColorHex(branchPageSettings.mapAccentColor)}
            onChange={(mapAccentColor) => {
              setBranchPageSettings((prev) => ({ ...prev, mapAccentColor }));
              void savePageSettings({ mapAccentColor });
            }}
            recommended={{
              label: 'Önerilen site rengi',
              value: sitePrimaryColor,
              hint: 'Genel ayarlardaki site ana rengi',
            }}
            options={SIMPLE_MAP_COLORS}
            allowEmpty
            emptyMeansRecommended
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={() => void savePageSettings()}
            disabled={savingPageSettings}
          >
            {savingPageSettings ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Kart Logoları (tüm şubelerde aynı)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Vitrin şube kartlarında marka ve ödeme ikonları olarak gösterilir. Değişiklikten sonra üstteki Kaydet’e basın.
        </Typography>
        <Grid container spacing={2}>
          {([
            { key: 'brandLogos' as const, title: 'Marka logoları', mode: 'brands' as const },
            { key: 'paymentIcons' as const, title: 'Ödeme ikonları', mode: 'payments' as const },
          ]).map((group) => (
            <Grid item xs={12} md={6} key={group.key}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={700}>{group.title}</Typography>
                <Button size="small" variant="outlined" onClick={() => openLogoLibrary(group.mode)}>
                  Ekle
                </Button>
              </Box>
              {branchPageSettings[group.key].length === 0 ? (
                <Typography variant="caption" color="text.secondary">Henüz eklenmedi.</Typography>
              ) : (
                branchPageSettings[group.key].map((logo, index) => (
                  <Box
                    key={`${group.key}-${logo.url}-${index}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}
                  >
                    <Box
                      component="img"
                      src={logo.url}
                      alt={logo.alt || group.title}
                      sx={{ width: 36, height: 24, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 0.5, bgcolor: 'background.paper' }}
                    />
                    <Typography variant="caption" noWrap sx={{ flex: 1 }}>{logo.alt || logo.url}</Typography>
                    <IconButton size="small" onClick={() => moveLogo(group.key, index, -1)} disabled={index === 0}>
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveLogo(group.key, index, 1)} disabled={index === branchPageSettings[group.key].length - 1}>
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeLogo(group.key, index)}>
                      <DeleteOutlineIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography>Şube Görünümü ({branches.length})</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={fillingMaps ? <CircularProgress size={14} /> : <LocationOnIcon />}
          onClick={() => void fillMissingMapsFromAddresses()}
          disabled={fillingMaps || branches.length === 0}
        >
          {fillingMaps
            ? `Konumlar dolduruluyor${fillingMapsProgress ? ` (${fillingMapsProgress})` : ''}...`
            : 'Eksik konumları adresten doldur'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Çalışma saatleri, görsel ve harita kaydedilince vitrindeki şube kartına ve detay popup’ına yansır.
        Adres varsa ve harita boşsa kayıt sırasında konum otomatik üretilir; Yol Tarifi bu konumu açar.
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Harita Önizleme ({previewMapPoints.length} konum)
        </Typography>
        <Box sx={{ height: 320, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <MapContainer
            center={[39.0, 35.0]}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            key={`preview-${previewMapPoints.length}`}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={previewMapPoints} />
            {previewMapPoints.map((p) => (
              <Marker key={`${p.id}-${p.lat}-${p.lng}`} position={[p.lat, p.lng]}>
                <Popup>
                  <strong>{p.name}</strong><br />
                  {p.city}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {branchPageSettings.pageTemplate === 'province-map'
            ? 'İl Haritası şablonunda vitrinde boyalı Türkiye haritası kullanılır; şubesi olan iller panelden seçilen harita renginde boyanır. Bu önizleme konum seçimi içindir.'
            : 'Klasik şablonda pinli harita, şehir filtresi ve altta tüm şube kartları kullanılır. Bu önizleme konum seçimi içindir.'}
        </Typography>
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700}>Filtreler</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {([
            { key: 'all' as const, label: `Tümü (${statusCounts.all})` },
            { key: 'active' as const, label: `Aktif olanları göster (${statusCounts.active})` },
            { key: 'inactive' as const, label: `Pasif olanları göster (${statusCounts.inactive})` },
          ]).map((item) => (
            <Chip
              key={item.key}
              size="small"
              label={item.label}
              color={statusFilter === item.key ? 'primary' : 'default'}
              variant={statusFilter === item.key ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(item.key)}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Şehir</InputLabel>
            <Select
              label="Şehir"
              value={cityFilter}
              onChange={(e) => setCityFilter(String(e.target.value))}
            >
              <MenuItem value="all">Tüm şehirler ({savedCities.length})</MenuItem>
              {savedCities.map((city) => (
                <MenuItem key={city} value={city}>{city}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Şube Ara"
            placeholder="Şube adı, şehir, ilçe, adres veya telefon..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {branches.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={allFilteredSelected}
                indeterminate={someFilteredSelected && !allFilteredSelected}
                onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                disabled={filteredBranches.length === 0 || bulkSaving}
              />
              <Typography variant="subtitle2" fontWeight={700}>Toplu işlemler</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedIds.size > 0
                  ? `${selectedIds.size} şube seçili`
                  : filteredBranches.length > 0
                    ? `Filtrelenen ${filteredBranches.length} şube`
                    : 'Uygulanacak şube yok'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={bulkTargets.length > 0 && inactiveTargets.length === 0}
                    disabled={bulkSaving || inactiveTargets.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) void applyBulkVisibility(true);
                    }}
                  />
                }
                label={inactiveTargets.length > 0 ? `Pasifleri aç (${inactiveTargets.length})` : 'Pasifleri aç'}
                sx={{ m: 0 }}
              />
              <Button
                size="small"
                variant="outlined"
                color="warning"
                disabled={bulkSaving || activeTargets.length === 0}
                onClick={() => setConfirmPassiveOpen(true)}
              >
                Pasif yap{activeTargets.length > 0 ? ` (${activeTargets.length})` : ''}
              </Button>
              {bulkSaving && <CircularProgress size={16} />}
            </Box>
          </Box>
        </Paper>
      )}

      {branches.length === 0 && (
            <Alert severity="warning">
              Henüz hiç şube yok. Şubeler sayfasından yeni şube ekleyin; ardından bu ekranda
              e-ticaret görünümünü düzenleyebilirsiniz.
            </Alert>
      )}

      {branches.length > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {filteredBranches.length === 0
              ? `0 / ${branches.length} şube`
              : `${(page - 1) * rowsPerPage + 1} - ${Math.min(page * rowsPerPage, filteredBranches.length)} / ${filteredBranches.length} şube`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Sayfa başına:</Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                const next = Number(e.target.value);
                setRowsPerPage(next);
                setPage(1);
              }}
              sx={{ minWidth: 78 }}
            >
              {[8, 12, 20, 30].map((size) => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}

      {pagedBranches.map((row) => (
            <Paper key={row.branchId} variant="outlined" sx={{ p: 1.25, mb: 1 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3.5}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(row.branchId)}
                      onChange={(e) => toggleRowSelected(row.branchId, e.target.checked)}
                      disabled={bulkSaving}
                      sx={{ mt: -0.5, mr: 0.25 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">{row.branchName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[row.city, row.district].filter(Boolean).join(' / ') || '—'}
                    </Typography>
                    {Boolean(row.address) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                        {row.address}
                      </Typography>
                    )}
                    {Boolean(row.phone) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Tel: {row.phone}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(row.isOpen24Hours)}
                            onChange={(e) =>
                              updateBranchField(row.branchId, {
                                isOpen24Hours: e.target.checked,
                                workingHours: e.target.checked ? '' : row.workingHours,
                              })
                            }
                          />
                        }
                        label="7/24"
                        sx={{ m: 0 }}
                      />
                      <TextField
                        size="small"
                        label="Çalışma saatleri"
                        placeholder="09:00 - 22:00"
                        disabled={Boolean(row.isOpen24Hours)}
                        value={row.isOpen24Hours ? '7/24 Açık' : (row.workingHours || '')}
                        onChange={(e) => updateBranchField(row.branchId, { workingHours: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 180, flex: 1 }}
                      />
                    </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          height: 40,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          px: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          {row.imageUrl ? (
                            <Box
                              component="img"
                              src={row.imageUrl}
                              alt={row.branchName}
                              sx={{ width: 26, height: 26, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <CollectionsOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                          )}
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {row.imageUrl ? 'Görsel seçildi' : 'Görsel seçilmedi'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          {row.imageUrl && (
                            <IconButton
                              size="small"
                              onClick={() => updateBranchField(row.branchId, { imageUrl: '' })}
                              title="Görseli temizle"
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openImageLibrary(row.branchId)}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            Seç
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Harita Linki (Google Maps)"
                        value={row.mapUrl || ''}
                        onChange={(e) => updateBranchField(row.branchId, { mapUrl: e.target.value })}
                        placeholder="https://maps.google.com/... (konum linki)"
                        InputProps={{
                          endAdornment: row.mapUrl?.trim() ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => window.open(normalizeMapUrlInput(row.mapUrl), '_blank', 'noopener,noreferrer')}
                                title="Harita linkini yeni sekmede aç"
                              >
                                <OpenInNewIcon fontSize="inherit" />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined
                        }}
                      />
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          startIcon={<LocationOnIcon />}
                          onClick={() => openMapPicker(row)}
                        >
                          Haritadan Seç
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={4} md={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Sıra"
                    value={row.displayOrder}
                    onChange={(e) => updateBranchField(row.branchId, { displayOrder: Number(e.target.value || 0) })}
                  />
                </Grid>

                <Grid item xs={4} md={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={row.showOnEcommerce}
                        onChange={(e) => updateBranchField(row.branchId, { showOnEcommerce: e.target.checked })}
                      />
                    }
                    label={row.showOnEcommerce ? 'Göster' : 'Gizli'}
                    sx={{ m: 0 }}
                  />
                </Grid>

                <Grid item xs={4} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    disabled={savingRow === row.branchId}
                    onClick={() => void saveBranchRow(row)}
                    sx={{ minWidth: 0, px: 1.25 }}
                  >
                    {savingRow === row.branchId ? '...' : 'Kaydet'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
      ))}

      {filteredBranches.length === 0 && branches.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Filtreye uygun şube bulunamadı.
        </Alert>
      )}

      {filteredBranches.length > rowsPerPage && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            size="small"
            page={page}
            count={totalPages}
            onChange={(_, val) => setPage(val)}
            color="primary"
          />
        </Box>
      )}

      {branches.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Kayıtlı şehirler:</Typography>
          {savedCities.length === 0 ? (
            <Typography variant="caption" color="text.secondary">—</Typography>
          ) : (
            savedCities.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                color={cityFilter === c ? 'primary' : 'default'}
                variant={cityFilter === c ? 'filled' : 'outlined'}
                onClick={() => setCityFilter((prev) => (prev === c ? 'all' : c))}
              />
            ))
          )}
        </Box>
      )}

      <ImageLibrarySelectionModal
        open={imageModalOpen}
        onClose={closeImageLibrary}
        onSave={handleImageLibrarySave}
        companyId={companyId}
        productName={
          imageModalMode === 'brands'
            ? 'Marka Logoları'
            : imageModalMode === 'payments'
              ? 'Ödeme İkonları'
              : (imageTargetRow?.branchName || 'Şube Görseli')
        }
        currentImageUrls={
          imageModalMode === 'brands'
            ? branchPageSettings.brandLogos.map((x) => x.url)
            : imageModalMode === 'payments'
              ? branchPageSettings.paymentIcons.map((x) => x.url)
              : (imageTargetRow?.imageUrl ? [imageTargetRow.imageUrl] : [])
        }
        productId={imageTargetRow ? String(imageTargetRow.branchId) : null}
        singleSelect={imageModalMode === 'branch'}
        maxSelection={imageModalMode === 'branch' ? 1 : 12}
      />

      <Dialog open={mapPickerOpen} onClose={closeMapPicker} maxWidth="md" fullWidth>
        <DialogTitle>
          Haritadan Konum Seç {mapTargetRow ? `- ${mapTargetRow.branchName}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Haritaya tıklayarak şube konumunu işaretleyin. Kaydedince Google Maps linki otomatik oluşturulur.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label="Konum Ara"
              placeholder="Örn: Düzce Hacıoğlu Şubesi"
              value={mapSearchQuery}
              onChange={(e) => setMapSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void searchLocationOnMap();
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={mapSearchLoading ? <CircularProgress size={14} /> : <SearchIcon />}
              onClick={() => void searchLocationOnMap()}
              disabled={mapSearchLoading}
            >
              Ara
            </Button>
            <Button
              variant="text"
              onClick={() => {
                const addrQuery = [mapTargetRow?.address, mapTargetRow?.district, mapTargetRow?.city, 'Türkiye']
                  .filter(Boolean)
                  .join(', ');
                setMapSearchQuery(addrQuery);
                void searchLocationOnMap(addrQuery);
              }}
              disabled={mapSearchLoading || !mapTargetRow?.address}
            >
              Adresten Bul
            </Button>
          </Box>
          {mapSearchError && (
            <Alert severity="warning" sx={{ mb: 1.25 }}>
              {mapSearchError}
            </Alert>
          )}
          {mapSearchResults.length > 0 && (
            <Box sx={{ mb: 1.25, maxHeight: 132, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
              {mapSearchResults.map((result, index) => (
                <Button
                  key={`${result.lat}-${result.lng}-${index}`}
                  fullWidth
                  size="small"
                  variant={mapDraftCoords?.lat === result.lat && mapDraftCoords?.lng === result.lng ? 'contained' : 'text'}
                  onClick={() => setMapDraftCoords({ lat: result.lat, lng: result.lng })}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.25 }}
                >
                  {result.label}
                </Button>
              ))}
            </Box>
          )}
          <Box sx={{ height: 420, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            {mapDraftCoords && (
              <MapContainer
                center={[mapDraftCoords.lat, mapDraftCoords.lng]}
                zoom={mapTargetRow?.mapUrl ? 15 : 6}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RecenterMap lat={mapDraftCoords.lat} lng={mapDraftCoords.lng} />
                <Marker position={[mapDraftCoords.lat, mapDraftCoords.lng]} />
                <MapClickHandler onPick={(lat, lng) => setMapDraftCoords({ lat, lng })} />
              </MapContainer>
            )}
          </Box>
          {mapDraftCoords && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Seçilen konum: {mapDraftCoords.lat.toFixed(6)}, {mapDraftCoords.lng.toFixed(6)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMapPicker}>Vazgeç</Button>
          <Button variant="contained" onClick={saveMapLocation} disabled={!mapDraftCoords}>
            Konumu Uygula
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmPassiveOpen} onClose={() => setConfirmPassiveOpen(false)}>
        <DialogTitle>Şubeleri pasif yap</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedIds.size > 0
              ? `Seçili ${activeTargets.length} şube e-ticarette gizlenecek. Devam edilsin mi?`
              : `Filtrelenen ${activeTargets.length} şube e-ticarette gizlenecek. Devam edilsin mi?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPassiveOpen(false)} disabled={bulkSaving}>Vazgeç</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={bulkSaving}
            onClick={() => void applyBulkVisibility(false)}
          >
            {bulkSaving ? 'İşleniyor...' : 'Pasif yap'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BranchSettingsComponent;
